<template>
    <div class="dashboard-container">
        <!-- Sidebar -->
        <aside class="sidebar">
            <div class="sidebar-header">
                <div class="sidebar-logo">
                    <div class="logo-icon">
                        <i class="fas fa-shield-alt"></i>
                    </div>
                    <div class="logo-text">
                        <span class="logo-title">Admin</span>
                        <span class="logo-subtitle">Panel</span>
                    </div>
                </div>
            </div>

            <nav class="sidebar-nav">
                <div class="nav-section">
                    <div class="nav-section-title">PRINCIPAL</div>
                    <a
                        href="#"
                        class="nav-item"
                        :class="{ active: activeSection === 'users' }"
                        @click.prevent="changeSection('users')"
                    >
                        <i class="fas fa-users"></i>
                        <span>Utilisateurs</span>
                    </a>

                    <a
                        href="#"
                        class="nav-item"
                        :class="{ active: activeSection === 'devices' }"
                        @click.prevent="changeSection('devices')"
                    >
                        <i class="fas fa-desktop"></i>
                        <span>Devices</span>
                    </a>

                    <a
                        href="#"
                        class="nav-item"
                        :class="{ active: activeSection === 'groups' }"
                        @click.prevent="changeSection('groups')"
                    >
                        <i class="fas fa-layer-group"></i>
                        <span>Groupes</span>
                    </a>
                </div>
            </nav>

            <div class="sidebar-footer">
                <div class="user-profile">
                    <div class="user-avatar-sidebar">
                        <i class="fas fa-user"></i>
                    </div>
                    <div class="user-info-sidebar">
                        <div class="user-name-sidebar">{{ user?.name || 'Admin' }}</div>
                        <div class="user-email-sidebar">{{ user?.email || 'admin@alibora.com' }}</div>
                    </div>
                </div>
                <button class="btn-back-home" @click="goBack" title="Retour à l'accueil">
                    <i class="fas fa-home"></i>
                </button>
            </div>
        </aside>

        <!-- Main Content -->
        <div class="main-content">
            <!-- Top Bar -->
            <header class="top-bar">
                <div class="top-bar-content">
                    <h1 class="page-title">
                        {{ sectionTitle }}
                    </h1>
                </div>
            </header>

            <!-- Page Content -->
            <main class="page-content">
                <transition name="fade" mode="out-in">
                    <UsersTable v-if="activeSection === 'users'" key="users" />
                    <DevicesTable
                        v-else-if="activeSection === 'devices'"
                        key="devices"
                        @devices-updated="onDevicesUpdated"
                    />
                    <GroupsTable v-else-if="activeSection === 'groups'" key="groups" />
                </transition>
            </main>
        </div>

        <!-- Mobile Sidebar Toggle -->
        <button class="sidebar-toggle" @click="sidebarOpen = !sidebarOpen">
            <i class="fas fa-bars"></i>
        </button>

        <!-- Mobile Overlay -->
        <div class="sidebar-overlay" :class="{ active: sidebarOpen }" @click="sidebarOpen = false"></div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import UsersTable from '@/components/admin/UsersTable.vue';
import DevicesTable from '@/components/admin/DevicesTable.vue';
import GroupsTable from '@/components/admin/GroupsTable.vue';

export default {
    name: 'AdminDashboard',
    components: {
        UsersTable,
        DevicesTable,
        GroupsTable
    },
    data() {
        return {
            activeSection: 'users',
            sidebarOpen: false,
            checkedAdmin: false
        };
    },
    computed: {
        ...mapState({
            user: state => state.auth?.authuser?.user
        }),
        sectionTitle() {
            const titles = {
                users: 'Gestion des Utilisateurs',
                devices: 'Gestion des Devices',
                groups: 'Gestion des Groupes'
            };
            return titles[this.activeSection] || 'Administration';
        }
    },
    async mounted() {
        try {
            await this.$store.dispatch('auth/loadFromLocalStorage');
        } catch (e) {
            try {
                await this.$store.dispatch('auth/checkToken');
            } catch (err) {
                const toast = useToast();
                toast.error('Veuillez vous connecter');
                this.$router.push({ name: 'Login' });
                return;
            }
        }
        this.checkAdminAccess();
    },
    methods: {
        checkAdminAccess() {
            if (this.checkedAdmin) return;
            const currentUser = this.$store.state.auth?.authuser?.user;
            const isAdmin = currentUser && (currentUser.is_admin == 1 || currentUser.is_admin === true);
            if (!isAdmin) {
                const toast = useToast();
                toast.error('Accès non autorisé');
                this.$router.push({ name: 'Home' });
            }
            this.checkedAdmin = true;
        },
        changeSection(section) {
            this.activeSection = section;
        },
        goBack() {
            this.$router.push({ name: 'Home' });
        },
        onDevicesUpdated() {
            console.log('Devices mis à jour');
        }
    }
};
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.dashboard-container {
    display: flex;
    height: 100vh;
    background: #f5f7fa;
    overflow: hidden;
}

/* Sidebar */
.sidebar {
    width: 220px;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
    position: relative;
    z-index: 100;
}

.sidebar-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.sidebar-logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: #6C5CE7;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.25rem;
}

.logo-text {
    display: flex;
    flex-direction: column;
}

.logo-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.2;
}

.logo-subtitle {
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.2;
}

.sidebar-nav {
    flex: 1;
    padding: 1rem 0;
    overflow-y: auto;
}

.nav-section {
    margin-bottom: 0;
}

.nav-section-title {
    padding: 0.75rem 1.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    color: #6b7280;
    text-decoration: none;
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.75rem;
    border-radius: 0.5rem;
}

.nav-item:hover {
    background: #f3f4f6;
    color: #374151;
}

.nav-item.active {
    background: #6C5CE7;
    color: white;
}

.nav-item i {
    width: 20px;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Sidebar Footer */
.sidebar-footer {
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

.user-profile {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: white;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
}

.user-avatar-sidebar {
    width: 36px;
    height: 36px;
    background: #6C5CE7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.user-info-sidebar {
    flex: 1;
    min-width: 0;
}

.user-name-sidebar {
    font-size: 0.875rem;
    font-weight: 600;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-email-sidebar {
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-back-home {
    width: 100%;
    padding: 0.625rem;
    border: none;
    background: white;
    color: #6b7280;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid #e5e7eb;
}

.btn-back-home:hover {
    background: #6C5CE7;
    color: white;
    border-color: #6C5CE7;
}

/* Main Content */
.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.top-bar {
    background: white;
    padding: 1.25rem 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid #e5e7eb;
}

.top-bar-content {
    display: flex;
    align-items: center;
}

.page-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
}

.page-content {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
}

/* Mobile */
.sidebar-toggle {
    display: none;
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    cursor: pointer;
    z-index: 1001;
}

.sidebar-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
}

.sidebar-overlay.active {
    display: block;
}

@media (max-width: 1024px) {
    .sidebar {
        position: fixed;
        left: -220px;
        top: 0;
        height: 100vh;
        z-index: 1000;
        transition: left 0.3s ease;
    }

    .sidebar.open {
        left: 0;
    }

    .sidebar-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .page-content {
        padding: 1rem;
    }

    .top-bar {
        padding: 1rem 1.5rem;
    }

    .page-title {
        font-size: 1.25rem;
    }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
